import { useMutation } from "@apollo/client/react"; import { Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import { useStepSave } from "../../../../hooks/useStepSave";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import CardBox from "../../CardBox/CardBox";

import SAVE_VISITA_NPID_NUTRICIONISTA, { adaptAddVisitaNpidNutricionistaInput } from "../../../../../../data/graphQL/mutations/Visitas/saveVisitaNpidNutricionista";
import { validateStepFields } from "../../../../utils/visitaStepValidation";

export default function ProgramaIntradialisisNutricionista({ readOnly, onSaveRef, onExposeFormatted, complicacionesCombo, esUrgencia }) {
  const [formData, setFormData] = useState({});

  const { visita, setVisita } = useVisita();

  const [save] = useMutation(SAVE_VISITA_NPID_NUTRICIONISTA);

  const formattedInputs = useMemo(() => {
    return inputs.map((input) => {
      const newInput = { ...input };

      if (input.name === 'suplementoOral' || input.name === 'oralDietaConvencional' || input.name === 'comentariosIndicacionOral') {
        newInput.hidden = !formData?.ingestaOral;
      }

      if (input.name === "complicaciones") {
        const ningunaId = complicacionesCombo?.find((c) => c.displayName.toLowerCase() === 'ninguna')?.id;
        newInput.options = complicacionesCombo?.map((opt) => {
          if (Array.isArray(formData.complicaciones)) {
            const hasNinguna = formData.complicaciones.includes(ningunaId);
            const hasOther = formData.complicaciones.some((id) => id !== ningunaId);
            if (hasNinguna) {
              // Si "ninguna" está seleccionada, deshabilitar todas menos "ninguna"
              return { ...opt, disabled: opt.id !== ningunaId };
            }
            if (hasOther) {
              // Si hay alguna distinta de "ninguna", deshabilitar "ninguna"
              return { ...opt, disabled: opt.id === ningunaId };
            }
          }
          return { ...opt, disabled: false };
        });
      }

      if (readOnly) newInput.disabled = true;
      if (esUrgencia) newInput.required = false;

      return newInput;
    });
  }, [readOnly, formData, complicacionesCombo, formData?.complicaciones, esUrgencia]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedInputs, formData });
    const { data } = await save({ variables: { input: adaptAddVisitaNpidNutricionistaInput(formData) } });
    const idVisitaNpid = data?.saveVisitaNpidNutricionista?.idVisitaNpid;
    if (!idVisitaNpid) throw new Error("Error al guardar");
    setVisita((prev) => ({ ...prev, ...formData, idVisitaNpid }));
  });

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(inputs);
      setFormData({
        ...filterVisitaData(visita, inputNames),
        idVisitaNpid: visita.idVisitaNpid,
        idVisita: visita.idVisita
      });
    }
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "programaIntradialisisNutricionista",
      stepName: "Programa Intradialisis (NPID)",
      formattedInputs
    });
  }, [onExposeFormatted, formattedInputs]);

  return (
    <CardBox>
      <Grid container spacing={2}>
        {formattedInputs.
          filter((input) => !input.hidden).
          map((input) =>
            <Grid size={{ xs: 12 }} key={input.name}>
              <RenderInputByTypeMobile
                input={input}
                value={formData[input.name]}
                handleChange={handleChange}
                row={input.type === "multiple_boolean"} />

            </Grid>
          )}
      </Grid>
    </CardBox>);

}

const inputs = [
  {
    name: "ingestaOral",
    displayName: "Ingesta oral",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "suplementoOral",
    displayName: "Suplemento oral",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "requerimientoCalorico",
    displayName: "Requerimiento calórico (Cal/Kg/d)",
    type: "float",
    required: true
  },
  {
    name: "oralDietaConvencional",
    displayName: "Ingesta oral por dieta convencional (Cal/Kg/d)",
    type: "float",
    required: true
  },
  {
    name: "comentariosIndicacionOral",
    displayName: "Comentarios sobre indicación oral",
    type: "long_string",
    maxLength: 1000
  },
  {
    name: "complicaciones",
    displayName: "Complicaciones",
    type: "multiple_select",
    required: true
  }];